import axios from 'axios';
import { API_URL } from '../../config/system/systemConfig';
import { ConversationModel } from '../../models/platform/conversationModel';
import { ResponseSuccessModel } from '../../models/system/responseSuccessModel';
import authHeader from '../../utils/authHeaderUtils';
import { handleAxiosError } from '../../utils/errorUtils';

class ConversationApi {

  async upsertMessageStream(
    conversation_id: string | null,
    content: string,
    onDelta: (text: string) => void,
    onDone: (data: {
      summary: string;
      suggested_question: string;
      assistant_message_id?: string;
      conversation_id?: string;
      blocks?: any[];
    }) => void,
    onError: (error: string) => void
  ) {
    try {
      const response = await fetch(`${API_URL}/api/conversations/messages/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeader()
        },
        body: JSON.stringify({
          conversation_id,
          content
        })
      });

      if (!response.body) {
        throw new Error("No response body");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");

      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Split SSE messages
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";

        for (const part of parts) {
          if (part.includes("event: delta")) {
            const dataLine = part.split("\n").find(line => line.startsWith("data:"));
            if (dataLine) {
              const parsed = JSON.parse(dataLine.replace("data: ", ""));
              onDelta(parsed.text);
            }
          }

          if (part.includes("event: done")) {
            const dataLine = part.split("\n").find(line => line.startsWith("data:"));
            if (dataLine) {
              const parsed = JSON.parse(dataLine.replace("data: ", ""));
              onDone(parsed);
            }
          }

          if (part.includes("event: error")) {
            const dataLine = part.split("\n").find(line => line.startsWith("data:"));
            if (dataLine) {
              const parsed = JSON.parse(dataLine.replace("data: ", ""));
              onError(parsed.message);
            }
          }
        }
      }

    } catch (err: any) {
      onError(err.message);
    }
  }



  async upsertMessage(conversation_id: string | null, role: 'user' | 'assistant', content: string) {
    try {
      const response = await axios.post(`${API_URL}/api/conversations/messages`, {
        conversation_id,
        role,
        content
      }, {
        headers: authHeader(),
      });

      return response.data as ResponseSuccessModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  }

  async getConversationById(conversation_id: string) {
    try {
      const response = await axios.get(`${API_URL}/api/conversations/${conversation_id}`, {
        headers: authHeader(),
      });

      return response.data as ResponseSuccessModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  }

  async getConversationsByAccount() {
    try {
      const response = await axios.get(`${API_URL}/api/conversations/me`, {
        headers: authHeader(),
      });

      return response.data as ResponseSuccessModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  }

  async getMessageLog(assistant_message_id: string) {
    try {
      const response = await axios.get(`${API_URL}/api/conversations/messages/log/${assistant_message_id}`, {
        headers: authHeader(),
      });

      return response.data as ResponseSuccessModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  }

  async deleteConversation(conversation_id: string) {
    try {
      const response = await axios.delete(`${API_URL}/api/conversations/${conversation_id}`, {
        headers: authHeader(),
      });

      return response.data as ResponseSuccessModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  }

  async updateConversationName(conversation_id: string, conversation_name: string) {
    try {
      const response = await axios.patch(`${API_URL}/api/conversations/${conversation_id}/name`, {
        conversation_name
      }, {
        headers: authHeader(),
      });

      return response.data as ResponseSuccessModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  }
}

const conversationApi = new ConversationApi();
export default conversationApi;
